import * as React from 'react'
import { NavLink, useHistory, useLocation } from 'react-router-dom'
import { Navigation, renderSearch } from 'navigation'
import { withNavigation } from 'hocs'
import { match as RouteMatch } from 'react-router-dom'
import { LucideIcon, LucideIconName } from 'utils/lucideIcon'
import { useDispatch } from 'react-redux'
import { closeSideBar } from './reducer'
import { Chevron } from 'uiComponents/chevron'
import classNames from 'classnames'
import './styles.scss'

interface MenuItemProps {
    children?: React.ReactNode
    path: string
    iconName?: LucideIconName
    image?: string | React.ReactNode
    text: string
    style?: object
    className?: string
    userpilot?: string
    navigation: Navigation
    match: RouteMatch<any>
    onClick?: (path: string) => void
    count?: number
    close?: boolean
    floatText?: string
    rightComponent?: React.ReactNode
}

function MenuItem(props: MenuItemProps) {
    const { path, iconName, image, text, children, className: itemClasses, userpilot, count, rightComponent } = props
    const originalQuery = props.navigation.query()
    const persistedQueryItems = ['dateRange', 'dateFrom', 'dateTo', 'period']
    const query = Object.keys(originalQuery)
        .filter((key) => persistedQueryItems.indexOf(key) > -1)
        .reduce((obj, key) => {
            obj[key] = originalQuery[key]
            return obj
        }, {})
    const persistedSearch = renderSearch(query)
    const [expanded, setExpanded] = React.useState<boolean>(false)
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()
    const sublinkListRef = React.useRef<HTMLUListElement>(null)
    const hasMounted = React.useRef(false)

    const covers = (pathname: string) => {
        const base = path.replace(/\/$/, '')
        return pathname === base || pathname.startsWith(`${base}/`)
    }

    React.useEffect(() => {
        if (props.children && covers(location.pathname)) {
            setExpanded(true)
        }
    }, [props.children, location.pathname])

    React.useEffect(() => {
        if (!hasMounted.current) {
            hasMounted.current = true
            return
        }
        if (props.close) {
            setExpanded(false)
        }
    }, [props.close])

    const firstSublinkTarget = () => {
        const link = sublinkListRef.current?.querySelector<HTMLAnchorElement>('a.menu-link')
        return link ? `${link.pathname}${link.search}` : null
    }

    const toggleExpanded = (e: React.MouseEvent<any>) => {
        if (props.children) {
            e.preventDefault()
            const willExpand = !expanded
            setExpanded(willExpand)
            if (willExpand) {
                const target = firstSublinkTarget()
                if (target && target !== `${location.pathname}${location.search}`) {
                    history.push(target)
                }
            }
        }

        if (props.onClick) {
            props.onClick(props.path)
        }

        if (!props.children) {
            dispatch(closeSideBar())
        }
    }

    return (
        <>
            <li data-userpilot={userpilot} data-label={text}>
                <NavLink
                    to={{ pathname: path, search: persistedSearch }}
                    onClick={toggleExpanded}
                    className={classNames('menu-link', itemClasses, {
                        'has-badge': !!props.floatText || !!rightComponent,
                    })}
                >
                    {iconName && (
                        <div className="icon-wrapper">
                            <LucideIcon iconName={iconName} size={16} />
                        </div>
                    )}
                    {image && (
                        <div className="image-wrapper">
                            {typeof image === 'string' ? <img className="image" src={image} /> : image}
                        </div>
                    )}
                    <div className="menu-label">
                        {props.floatText && <div className="float-text">{props.floatText}</div>}
                        {text}
                        {!!props.count && <div className="counter">{count}</div>}
                    </div>
                    {rightComponent && <div className="menu-right">{rightComponent}</div>}
                    {!!children && (
                        <Chevron
                            className="expander"
                            direction={expanded ? 'up' : 'down'}
                            size={12}
                            strokeWidth={1.5}
                        />
                    )}
                </NavLink>
            </li>
            {children && (
                <li className={classNames({ sublinks: true, show: expanded })}>
                    <div className="sublink-area">
                        <ul className="sublink-list" ref={sublinkListRef}>
                            {children}
                        </ul>
                    </div>
                </li>
            )}
        </>
    )
}

export default withNavigation(MenuItem)
